import React, { useState, useEffect } from 'react';
import { storage } from './firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

const API_URL = process.env.NODE_ENV === 'production'
  ? `${process.env.REACT_APP_API_URL}/api/vehicles`   // ← REPLACE with real backend (e.g. https://st-motor-1.onrender.com/api/vehicles)
  : '/api/vehicles';
function VehicleList() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    displayPrice: '',
    status: '',
    color: '',
    details: '',
    videoURL: ''
  });

  const [imageFile, setImageFile] = useState(null);
  const [brochureFile, setBrochureFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [formMessage, setFormMessage] = useState('');

  const fetchVehicles = async () => {
  setLoading(true);

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);

  try {
    const res = await fetch(API_URL, {
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': process.env.REACT_APP_VEHICLES_API_KEY || '',
      },
    });

    clearTimeout(timeoutId);

    if (!res.ok) {
      if (res.status === 401) throw new Error('Unauthorized - check API key');
      throw new Error(`HTTP error! Status: ${res.status}`);
    }

    const result = await res.json();
    setVehicles(result.data || []);
  } catch (err) {
    console.error('Failed to load vehicles:', err);
    alert('Failed to load vehicles: ' + (err.message || 'Unknown error'));
  } finally {
    setLoading(false);
  }
};

useEffect(() => {
  fetchVehicles();
}, []);   

const handleDelete = async (vehicle) => {
  if (!window.confirm('Are you sure you want to delete this vehicle? This will permanently remove its image and brochure from storage.')) {
    return;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);

  try {
    // Delete image
    if (vehicle.imageURL) {
      const imageRef = ref(storage, vehicle.imageURL);
      await deleteObject(imageRef).catch(err => {
        if (err.code !== 'storage/object-not-found') throw err;
      });
    }

    // Delete brochure
    if (vehicle.brochureURL) {
      const brochureRef = ref(storage, vehicle.brochureURL);
      await deleteObject(brochureRef).catch(err => {
        if (err.code !== 'storage/object-not-found') throw err;
      });
    }

    // Delete from DB
    const res = await fetch(`${API_URL}/${vehicle._id}`, {
      method: 'DELETE',
      headers: {
        'X-API-Key': process.env.REACT_APP_VEHICLES_API_KEY || '',
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to delete vehicle');
    }

    fetchVehicles();
    alert('Vehicle and files deleted successfully!');
  } catch (err) {
    console.error('Delete failed:', err);
    alert('Error deleting vehicle: ' + (err.message || 'Unknown error'));
  }
};
  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleBrochureChange = (e) => {
    const file = e.target.files[0];
    if (file) setBrochureFile(file);
  };

  const handleFormSubmit = async (e) => {
  e.preventDefault();
  if (!imageFile && !editingVehicle) {
    setFormMessage('Please upload an image');
    return;
  }

  setFormLoading(true);
  setFormMessage('');

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 15000); // longer for uploads

  try {
    let imageURL = editingVehicle?.imageURL || null;
    if (imageFile) {
      const imageRef = ref(storage, `vehicles/images/${Date.now()}_${imageFile.name}`);
      await uploadBytes(imageRef, imageFile);
      imageURL = await getDownloadURL(imageRef);
    }

    let brochureURL = editingVehicle?.brochureURL || null;
    if (brochureFile) {
      const brochureRef = ref(storage, `vehicles/brochures/${Date.now()}_${brochureFile.name}`);
      await uploadBytes(brochureRef, brochureFile);
      brochureURL = await getDownloadURL(brochureRef);
    }

    const vehicleData = {
      name: formData.name.trim(),
      price: formData.price.trim(),
      displayPrice: formData.displayPrice?.trim() || '',
      status: formData.status.trim(),
      color: formData.color.trim(),
      details: formData.details.trim(),
      imageURL,
      brochureURL,
      videoURL: formData.videoURL?.trim() || null
    };

    const method = editingVehicle ? 'PUT' : 'POST';
    const url = editingVehicle ? `${API_URL}/${editingVehicle._id}` : API_URL;

    const res = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': process.env.REACT_APP_VEHICLES_API_KEY || '',
      },
      body: JSON.stringify(vehicleData),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!res.ok) {
      const result = await res.json();
      throw new Error(result.errors?.join(', ') || result.message || 'Failed to save vehicle');
    }

    setFormMessage(`✅ Vehicle ${editingVehicle ? 'updated' : 'added'} successfully!`);
    resetForm();
    fetchVehicles();
  } catch (err) {
    console.error('Form submit error:', err);
    setFormMessage('❌ ' + (err.message || 'Failed to save vehicle'));
  } finally {
    setFormLoading(false);
  }
};

  const resetForm = () => {
    setFormData({
      name: '',
      price: '',
      displayPrice: '',
      status: '',
      color: '',
      details: '',
      videoURL: ''
    });
    setImageFile(null);
    setBrochureFile(null);
    setImagePreview('');
    setShowForm(false);
    setEditingVehicle(null);
  };

  // FIXED: Properly load displayPrice
  const openEditForm = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      name: vehicle.name || '',
      price: vehicle.price || '',
      displayPrice: vehicle.displayPrice || '', // ← Ensures value is loaded
      status: vehicle.status || '',
      color: vehicle.color || '',
      details: vehicle.details || '',
      videoURL: vehicle.videoURL || ''
    });
    setImagePreview(vehicle.imageURL || '');
    setShowForm(true);
  };

  const formatPrice = (price) => {
    if (!price) return '';
    const num = parseInt(price.replace(/[^0-9]/g, ''), 10);
    if (isNaN(num)) return price;
    return num.toLocaleString();
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8f9fa',
        color: '#333',
        fontSize: '1.8rem'
      }}>
        Loading vehicles...
      </div>
    );
  }
if (error) {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f8f9fa',
      color: '#dc3545',
      textAlign: 'center',
      padding: '2rem'
    }}>
      <h2 style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>Error Loading Vehicles</h2>
      <p style={{ fontSize: '1.3rem', maxWidth: '600px' }}>{error}</p>
      <button
        onClick={fetchVehicles}
        style={{
          marginTop: '2rem',
          padding: '1rem 2.5rem',
          background: '#2c5282',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '1.1rem',
          cursor: 'pointer'
        }}
      >
        Retry
      </button>
    </div>
  );
}


  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8f9fa',
      color: '#333',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <div style={{ padding: '6rem 2rem 8rem', maxWidth: '1600px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '6rem' }}>
          <div style={{
            display: 'inline-block',
            color: '#666',
            fontSize: '0.9rem',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            marginBottom: '1.5rem',
            fontWeight: '600'
          }}>
            Admin Dashboard
          </div>
          <h1 style={{
            fontSize: '4rem',
            fontWeight: '300',
            marginBottom: '1.5rem',
            color: '#222',
            letterSpacing: '-0.02em'
          }}>
            Manage <span style={{ fontWeight: '600', color: '#2c5282' }}>Vehicles</span>
          </h1>
        </div>

        {/* ADD NEW VEHICLE BUTTON */}
        {!showForm && (
          <div style={{ textAlign: 'center', marginBottom: '6rem' }}>
            <button
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              style={{
                background: '#2c5282',
                color: 'white',
                padding: '1.25rem 4rem',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              ➕ Add New Vehicle
            </button>
          </div>
        )}

        {/* FORM */}
        {showForm && (
          <div style={{ background: 'white', padding: '40px', borderRadius: '16px', boxShadow: '0 10px 40px rgba(0,0,0,0.1)', maxWidth: '900px', margin: '0 auto' }}>
            <h2 style={{ textAlign: 'center', color: '#2c5282', marginBottom: '30px' }}>
              {editingVehicle ? 'Edit Vehicle' : 'Add New Vehicle'}
            </h2>

            {formMessage && (
              <div style={{
                padding: '15px',
                marginBottom: '20px',
                borderRadius: '8px',
                background: formMessage.startsWith('✅') ? '#d4edda' : '#f8d7da',
                color: formMessage.startsWith('✅') ? '#155724' : '#721c24'
              }}>
                {formMessage}
              </div>
            )}

            <form onSubmit={handleFormSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Name *</label>
                  <input type="text" name="name" value={formData.name} onChange={handleFormChange} required style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ccc' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Price *</label>
                  <input type="text" name="price" value={formData.price} onChange={handleFormChange} required placeholder="e.g. 5000000" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ccc' }} />
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Display Price (optional)</label>
                <input type="text" name="displayPrice" value={formData.displayPrice} onChange={handleFormChange} placeholder="e.g. Nu.5,000,000" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ccc' }} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Status *</label>
                  <input type="text" name="status" value={formData.status} onChange={handleFormChange} required placeholder="e.g. Available" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ccc' }} />
                </div>
                <div>
  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Color</label>
  <input 
    type="text" 
    name="color" 
    value={formData.color} 
    onChange={handleFormChange} 
    placeholder="Diamond Black (optional)" 
    style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ccc' }} 
  />
</div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Details </label>
                <textarea name="details" value={formData.details} onChange={handleFormChange}rows="5" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ccc' }} />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Image * {editingVehicle && '(leave blank to keep current)'}</label>
                <input type="file" accept="image/*" onChange={handleImageChange} {...(!editingVehicle && { required: true })} />
                {imagePreview && <img src={imagePreview} alt="Preview" style={{ marginTop: '10px', maxWidth: '100%', maxHeight: '400px', borderRadius: '8px' }} />}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Brochure PDF (optional)</label>
                <input type="file" accept=".pdf" onChange={handleBrochureChange} />
              </div>

              <div style={{ marginBottom: '30px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Video URL (optional)</label>
                <input type="url" name="videoURL" value={formData.videoURL} onChange={handleFormChange} placeholder="https://youtube.com/watch?v=..." style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ccc' }} />
              </div>

              <div style={{ display: 'flex', gap: '15px' }}>
                <button type="submit" disabled={formLoading} style={{ flex: 1, padding: '16px', background: '#2c5282', color: 'white', fontWeight: 'bold', border: 'none', borderRadius: '8px' }}>
                  {formLoading ? 'Saving...' : editingVehicle ? 'Update Vehicle' : 'Add Vehicle'}
                </button>
                <button type="button" onClick={resetForm} style={{ padding: '16px', background: '#666', color: 'white', border: 'none', borderRadius: '8px' }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* VEHICLE GRID */}
        {!showForm && vehicles.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))',
            gap: '3rem'
          }}>
            {vehicles.map((vehicle) => (
              <div
                key={vehicle._id}
                onMouseEnter={() => setHoveredCard(vehicle._id)}
                onMouseLeave={() => setHoveredCard(null)}
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: hoveredCard === vehicle._id
                    ? '0 20px 40px rgba(0, 0, 0, 0.1)'
                    : '0 8px 25px rgba(0, 0, 0, 0.08)',
                  transition: 'all 0.4s ease',
                  transform: hoveredCard === vehicle._id ? 'translateY(-8px)' : 'translateY(0)'
                }}
              >
                <div style={{ position: 'relative', overflow: 'hidden', height: '320px' }}>
                  <img
                    src={vehicle.imageURL}
                    alt={vehicle.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.6s ease',
                      transform: hoveredCard === vehicle._id ? 'scale(1.08)' : 'scale(1)'
                    }}
                  />
                </div>

                <div style={{ padding: '2.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
                    <div>
                      <h3 style={{
                        fontSize: '1.8rem',
                        fontWeight: '500',
                        color: '#222',
                        marginBottom: '0.5rem'
                      }}>
                        {vehicle.name}
                      </h3>
                      <span style={{
                        display: 'inline-block',
                        background: '#e6f4ff',
                        color: '#2c5282',
                        padding: '0.4rem 1rem',
                        borderRadius: '20px',
                        fontSize: '0.85rem',
                        fontWeight: '600'
                      }}>
                        {vehicle.status}
                      </span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{
                        fontSize: '1.8rem',
                        fontWeight: '400',
                        color: '#2c5282'
                      }}>
                        Nu. {formatPrice(vehicle.price)}
                      </div>
                    </div>
                  </div>

                  {/* DISPLAY PRICE ABOVE COLOR */}
                  <div style={{
                    padding: '1.5rem 0',
                    borderTop: '1px solid #eee',
                    borderBottom: '1px solid #eee',
                    marginBottom: '1.5rem',
                    color: '#555',
                    lineHeight: '1.8'
                  }}>
                    {vehicle.displayPrice && vehicle.displayPrice.trim() !== '' && (
                      <p><strong>Display Price:</strong> Nu. {formatPrice(vehicle.displayPrice)}</p>
                    )}
                    <p><strong>Color:</strong> {vehicle.color}</p>
                    <p><strong>Details:</strong> {vehicle.details}</p>
                  </div>

                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '2rem' }}>
                    {vehicle.brochureURL && (
                      <a href={vehicle.brochureURL} target="_blank" rel="noopener noreferrer" style={{ flex: 1, padding: '1rem', background: '#2c5282', color: 'white', textAlign: 'center', borderRadius: '8px', textDecoration: 'none', fontWeight: '600' }}>
                        View Brochure
                      </a>
                    )}
                    {vehicle.videoURL && (
                      <a href={vehicle.videoURL} target="_blank" rel="noopener noreferrer" style={{ flex: 1, padding: '1rem', background: '#f8f9fa', color: '#2c5282', textAlign: 'center', border: '2px solid #2c5282', borderRadius: '8px', textDecoration: 'none', fontWeight: '600' }}>
                        Watch Video
                      </a>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <button
                      onClick={() => openEditForm(vehicle)}
                      style={{
                        flex: 1,
                        padding: '1.2rem',
                        background: '#3182ce',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Edit
                    </button>

                    <button
                      onClick={() => handleDelete(vehicle)}
                      style={{
                        flex: 1,
                        padding: '1.2rem',
                        background: '#e53e3e',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!showForm && vehicles.length === 0 && (
          <div style={{ textAlign: 'center', padding: '120px', color: '#888' }}>
            <h2 style={{ fontSize: '2.5rem', marginBottom: '20px', color: '#444' }}>
              No vehicles in collection
            </h2>
            <p style={{ fontSize: '1.3rem' }}>
              Click "Add New Vehicle" to start building your fleet
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default VehicleList;